
import "./Header.css";

import { useEffect, useState } from "react";
import { onMessage } from "firebase/messaging";
import { Button } from "primereact/button";

import Logo from "../logo/Logo";
import Profile from "../profile/Profile";
import DownloadButton from "../downloadButton/DownloadButton";
import Tracker from "../tracker/Tracker";
import Notifications from "../notifications/Notifications";

import { generateToken, messaging } from "../../firebase";
import sound from "../../medias/sounds/notifiation.mp3";
import { updateUserRequest } from "../../services/wisdomServices/userService";
import { getSessionItem } from "../../utils/helper/session";
import { getNotificationsRequest } from "../../services/notificationServices/notificationService";

/**
 * @function Header
 * @param {string} title
 * @param {Function} onOpenWorkforce
 * @returns {JSX.Element}
 */
function Header({ title, onOpenWorkforce }) {
    const user = getSessionItem("User");
    const version = import.meta.env.VITE_APP_VERSION_NUMBER || "1.0";

    const [messages, setMessages] = useState([]);
    const [isLoadingNotifications, setIsLoadingNotifications] = useState(user?.roleName !== "Applicant");

    /**
     * Fetches unread notifications for the logged-in user.
     */
    const fetchData = async () => {
      setIsLoadingNotifications(true);

      try {
          const response = await getNotificationsRequest(user.email, false);
          setMessages(response.data ?? []);
      } catch (error) {
          console.error("Error fetching notifications:", error);
      } finally {
          setIsLoadingNotifications(false);
      }
    };

    /**
     * Plays the notification sound.
     */
    const playNotificationSound = () => {
        const audio = new Audio(sound);

        audio.play().catch(error => {
            console.error("Error playing notification sound:", error);
        });
    };

    /**
     * Updates the user's web push token.
     *
     * @param {number} id
     * @param {string} role
     * @param {string} token
     */
    const updateUserToken = async (id, role, token) => {
        const keys = {
            role,
            webToken: token
        };

        await updateUserRequest(id, keys);
    };

    useEffect(() => {
        if (!user || user.roleName === "Applicant") {
            return;
        }

        let unsubscribe = () => {};
        let isMounted = true;

        const init = async () => {
            try {
                const token = await generateToken();

                if (token && token !== user.webToken) {
                    await updateUserToken(user.id, user.roleName, token);
                }
            } catch (error) {
                console.error("Error initializing Firebase token:", error);
            }

            if (!isMounted) return;

            await fetchData();

            if (!isMounted) return;

            try {
                unsubscribe = onMessage(messaging, async payload => {
                    const idFromPayload =
                        payload?.data?.id ||
                        payload?.data?._id ||
                        null;

                    const pathFromPayload =
                        payload?.data?.path ||
                        payload?.fcmOptions?.link ||
                        "";

                    const messageObj = {
                        _id: idFromPayload,
                        content:
                            payload?.data?.body ||
                            payload?.notification?.body ||
                            "(No content)",
                        path: pathFromPayload,
                        isRead: false
                    };

                    playNotificationSound();

                    if (messageObj._id) {
                        setMessages(prev => [messageObj, ...prev]);
                    } else {
                        await fetchData();
                    }
                });
            } catch (error) {
                console.error("Error subscribing to notifications:", error);
            }
        };

        init();

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, []);

    return (
        <div className="header">
            <div className="align-left">
                <Logo />

                <h1 className="pageName">
                    {title}
                    <span className="global-app-version">
                        (v{version})
                    </span>
                </h1>
            </div>

            <div className="align-right">
                {user?.webtrackerEnabled && <Tracker />}

                <div className="download-button" id="header-downloads">
                    {user?.roleName !== "Customer" &&
                        user?.roleName !== "Applicant" &&
                        !user?.disabled && (
                            <DownloadButton />
                        )}
                </div>

                <Notifications
                    messages={messages}
                    isLoading={isLoadingNotifications}
                />

                <Profile onOpenWorkforce={onOpenWorkforce} />
            </div>
        </div>
    );
}

export default Header;
